import React, { useState, useEffect, useCallback, useRef } from 'react';
import { convertToFullTimeString, asFullTimeField } from '~/util/time';
import { format } from '~/util/date';
import { SelectedOption, Option } from '~/components/molecule/Dropdown';
import DropdownListContainer from '~/components/molecule/Dropdown/components/DropdownListContainer';
import Input, { Props as InputProps } from '~/components/molecule/Input';
import styled from 'styled-components';

type Props = Omit<InputProps, 'icon' | 'onChange'> & {
  onChange: (value: string | null) => void;
};

const TimePicker: React.FC<Props> = ({
  value,
  width,
  required,
  externalErrors,
  label,
  onChange,
  disabled,
  dataTestId,
}) => {
  const openerRef = useRef<HTMLDivElement | null>(null);
  const [dropdownListOpen, setDropdownListOpen] = useState(false);
  const [internalValue, setInternalValue] = useState(value);

  const selectOption = (selectedOption: SelectedOption) => {
    const newValue = selectedOption.option.label
      ? (selectedOption.option.label as string)
      : null;
    setInternalValue(newValue);
    onChange(newValue);
    setDropdownListOpen(false);
  };

  const hideList = () => setDropdownListOpen(false);
  const showList = () => setDropdownListOpen(true);

  const ensureFullTimefield = useCallback(() => {
    if (!internalValue) return;
    const fullValue = asFullTimeField(String(internalValue));
    if (fullValue !== internalValue) {
      setInternalValue(fullValue);
      onChange(fullValue);
    }
  }, [internalValue, onChange]);

  // Update internal value when external value changes
  useEffect(() => {
    if (value !== internalValue) {
      setInternalValue(value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const options = getTimeOptions();
  let selectedOptionIdx: number | undefined = undefined;

  const currentTimeDate = format(new Date(), 'H:mm');
  const timeValue = internalValue || getRoundedTime(currentTimeDate);

  options.forEach((item, index) => {
    if (item.key === timeValue) {
      selectedOptionIdx = index;
    }
  });

  const middleIndex = Math.floor(options.length / 2);

  return (
    <Container>
      <StyledInput
        value={internalValue}
        width={width || '100%'}
        placeholder="hh:mm"
        onFocus={showList}
        onBlur={ensureFullTimefield}
        readOnly
        icon={{ name: 'clock' }}
        inputContainerRef={openerRef}
        required={required}
        externalErrors={externalErrors}
        label={label}
        disabled={disabled}
        dataTestId={dataTestId}
      />

      <DropdownListContainer
        dropdownListOpen={dropdownListOpen}
        selectedOptionIdx={selectedOptionIdx}
        indexToDisplayOnOpen={middleIndex}
        options={options}
        onChange={selectOption}
        onClose={hideList}
        openerRef={openerRef}
        onClickOutside={hideList}
        positionerRootId="time-picker"
      />
    </Container>
  );
};

const Container = styled.div`
  cursor: pointer;
`;

const StyledInput = styled(Input)`
  cursor: pointer;

  /* Hide the calendar picker */
  input[type='time']::-webkit-calendar-picker-indicator {
    display: none;
  }
`;

const getTimeOptions = (): Array<Option> => {
  const times: Array<Option> = [];
  for (let h = 0; h < 24; h++) {
    for (let q = 0; q < 4; q++) {
      const time = convertToFullTimeString(h, q * 15);
      times.push({
        label: time,
        payload: time,
        key: `key-${time}`,
      });
    }
  }

  const endTime = {
    label: '23:59',
    payload: '23:59',
    key: 'key-23:59',
  };

  return [...times, endTime];
};

/**
 * This function rounds the time to the nearest 30 minutes
 * @param timeValue - The time to round
 * @returns The rounded time
 */
export const getRoundedTime = (timeValue: string): string => {
  const minsIndex = timeValue.indexOf(':') + 1;
  const hours = timeValue.slice(0, minsIndex - 1);
  const mins = parseInt(timeValue.slice(minsIndex));

  const roundedMins = mins / 30 > 1 ? '30' : '00';
  return `${hours}:${roundedMins}`;
};

export default TimePicker;
